import type { ICellRendererParams, ColDef, ValueGetterParams, GetQuickFilterTextParams } from 'ag-grid-community'
import type { ReactElement } from 'react'
import type { Menu, MenuItem } from '~/components/ActionMenu'
import type { IAppManagement } from '~/types/application-management'

import ActionMenu from '~/components/ActionMenu'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { DEVICE_TYPE } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { appKeyDeviceTypeToString } from '~/utils/application'
import { actionsColDef, applicationColDef, textColDef } from '~/utils/table'

type TData = IAppManagement

export interface IContext {
  handleRedirect: (path: string) => void
}

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<TData, void, IContext>): ReactElement => {
  const { dispatchToSearchParams } = useSearchParamsContext()

  if (data) {
    const { android, app_key, ios } = data

    const { handleRedirect } = context

    const androidActionMenuItem: MenuItem[] = android
      ? [
          {
            action: () => {
              const application = appKeyDeviceTypeToString(app_key, DEVICE_TYPE.ANDROID)
              dispatchToSearchParams({ type: 'update', value: { app: application } })
              handleRedirect(`/summary?app=${application}`)
            },
            label: 'Display Android App Summary',
            type: 'internal',
          },
        ]
      : []

    const iosActionMenuItem: MenuItem[] = ios
      ? [
          {
            action: () => {
              const application = appKeyDeviceTypeToString(app_key, DEVICE_TYPE.IOS)
              dispatchToSearchParams({ type: 'update', value: { app: application } })
              handleRedirect(`/summary?app=${application}`)
            },
            label: 'Display iOS App Summary',
            type: 'internal',
          },
        ]
      : []

    const menu: Menu = {
      navigations: [
        ...androidActionMenuItem,
        ...iosActionMenuItem,
        {
          action: () => handleRedirect(`/apps-management/${app_key}`),
          label: 'Display App Details',
          type: 'internal',
        },
      ],
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const columns: ColDef[] = [
  applicationColDef({
    field: 'default_display_name',
    getQuickFilterText: ({ data }: GetQuickFilterTextParams<TData>): string => data?.default_display_name || '',
    minWidth: 52,
    tooltipField: 'default_display_name',
    valueGetter: ({ data }: ValueGetterParams<TData>): Record<string, string | undefined> => {
      if (data) {
        const { default_display_name, icon_file_id } = data

        return {
          app_icon_url: icon_file_id ? `${FASTLY_PATH}${icon_file_id}` : undefined,
          name: default_display_name,
        }
      }

      return {}
    },
    width: 330,
  }),
  textColDef(TextCellRenderer, {
    field: 'app_key',
    headerName: 'Key',
    minWidth: 90,
    width: 170,
  }),
  textColDef(TextCellRenderer, {
    field: 'prefix',
    headerName: 'Prefix',
    minWidth: 90,
    width: 90,
  }),
  textColDef(TextCellRenderer, {
    field: 'default_cluster_id',
    headerName: 'Default Cluster ID',
    minWidth: 90,
    width: 170,
  }),
  textColDef(TextCellRenderer, {
    field: 'gcp_id',
    headerName: 'Google Cloud Platform ID',
    minWidth: 90,
    width: 250,
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
